import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  signInWithEmail,
  signUpWithEmail,
  resetPassword,
  parseFirebaseError,
} from '../../api/firebase';

// ── Helpers ────────────────────────────────────────────────────────────────────
const isEmailVal = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Google icon
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Eye icon for show/hide password
const EyeIcon = ({ show }) => show
  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;

// ── Main Component ─────────────────────────────────────────────────────────────
export default function LoginModal({ open, onClose }) {
  const { signIn } = useAuth();

  // 'signin' | 'signup'
  const [mode, setMode]         = useState('signin');

  // Shared state — email only
  const [email, setEmail]       = useState('');
  const [name, setName]         = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirm] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [showCPw, setShowCPw]   = useState(false);

  // UI state
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const emailRef  = useRef(null);

  // Lock scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Auto-focus email field on open
  useEffect(() => {
    if (open) {
      setTimeout(() => emailRef.current?.focus(), 80);
      reset();
    }
  }, [open, mode]);

  function reset() {
    setEmail(''); setName(''); setPassword(''); setConfirm('');
    setShowPw(false); setShowCPw(false); setBusy(false);
    setError(''); setSuccess(''); setForgotSent(false);
  }

  function switchMode(m) { setMode(m); reset(); }

  // ── Google ────────────────────────────────────────────────────────────────────
  async function handleGoogle() {
    setBusy(true); setError('');
    try {
      await signIn();
      onClose?.();
    } catch (e) {
      setError(parseFirebaseError(e.code) || e.message);
    } finally { setBusy(false); }
  }

  // ── Email Sign In ─────────────────────────────────────────────────────────────
  async function handleEmailSignIn() {
    if (!isEmailVal(email)) { setError('Please enter a valid email address.'); return; }
    if (!password) { setError('Please enter your password.'); return; }
    setBusy(true); setError('');
    try {
      await signInWithEmail(email, password);
      onClose?.();
    } catch (e) {
      setError(parseFirebaseError(e.code) || e.message);
    } finally { setBusy(false); }
  }

  // ── Email Sign Up ─────────────────────────────────────────────────────────────
  async function handleEmailSignUp() {
    if (!name.trim()) { setError('Please enter your full name.'); return; }
    if (!isEmailVal(email)) { setError('Please enter a valid email address.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPw) { setError('Passwords do not match.'); return; }
    setBusy(true); setError('');
    try {
      await signUpWithEmail(email, password, name.trim());
      onClose?.();
    } catch (e) {
      setError(parseFirebaseError(e.code) || e.message);
    } finally { setBusy(false); }
  }

  // ── Forgot Password ───────────────────────────────────────────────────────────
  async function handleForgotPassword() {
    if (!isEmailVal(email)) { setError('Enter your email above first, then click Forgot Password.'); return; }
    setBusy(true); setError('');
    try {
      await resetPassword(email);
      setForgotSent(true);
      setSuccess(`Reset link sent to ${email}`);
    } catch (e) {
      setError(parseFirebaseError(e.code) || e.message);
    } finally { setBusy(false); }
  }

  // ── Main submit dispatcher ────────────────────────────────────────────────────
  function handleSubmit(e) {
    e?.preventDefault();
    if (mode === 'signin') handleEmailSignIn();
    else handleEmailSignUp();
  }

  if (!open) return null;

  const isSignIn = mode === 'signin';

  const btnLabel = () => {
    if (busy) return isSignIn ? 'Signing in…' : 'Creating account…';
    return isSignIn ? 'Sign In' : 'Create Account';
  };

  return (
    <div className="lm-backdrop">
      <div className="lm-card" onClick={e => e.stopPropagation()}>
        {/* Close */}
        <button className="lm-close" onClick={onClose} aria-label="Close">✕</button>

        {/* Logo */}
        <div className="lm-logo">
          <img src="/assets/brand/logo.png" alt="Shortflix" style={{ height: 34 }} />
        </div>

        {/* Title */}
        <h2 className="lm-title">
          {isSignIn ? 'Sign in to your account' : 'Create your account'}
        </h2>

        {/* Google */}
        <button className="lm-google-btn" onClick={handleGoogle} disabled={busy}>
          <GoogleIcon />
          {busy ? 'Please wait…' : 'Continue with Google'}
        </button>

        {/* Divider */}
        <div className="lm-divider"><span>OR</span></div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate autoComplete="off">

          {/* Name — sign up only, slides in */}
          {!isSignIn && (
            <div className="lm-field-wrap lm-field-slide">
              <input
                className="lm-input"
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={e => { setName(e.target.value); setError(''); }}
                autoComplete="name"
              />
            </div>
          )}

          {/* Email field */}
          <div className="lm-field-wrap">
            <input
              ref={emailRef}
              className="lm-input"
              type="email"
              inputMode="email"
              placeholder="Email address"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              autoComplete="username"
            />
          </div>

          {/* Password field */}
          <div className="lm-field-wrap lm-field-slide">
            <input
              className="lm-input"
              type={showPw ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              autoComplete={isSignIn ? 'current-password' : 'new-password'}
            />
            <button type="button" className="lm-eye" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
              <EyeIcon show={showPw} />
            </button>
          </div>

          {/* Sign up → confirm password field */}
          {!isSignIn && (
            <div className="lm-field-wrap lm-field-slide">
              <input
                className="lm-input"
                type={showCPw ? 'text' : 'password'}
                placeholder="Confirm Password"
                value={confirmPw}
                onChange={e => { setConfirm(e.target.value); setError(''); }}
                autoComplete="new-password"
              />
              <button type="button" className="lm-eye" onClick={() => setShowCPw(v => !v)} tabIndex={-1}>
                <EyeIcon show={showCPw} />
              </button>
            </div>
          )}

          {/* Forgot password — sign in only */}
          {isSignIn && !forgotSent && (
            <div className="lm-forgot-wrap">
              <button type="button" className="lm-forgot-btn" onClick={handleForgotPassword} disabled={busy}>
                Forgot Password?
              </button>
            </div>
          )}

          {/* Error / success messages */}
          {error   && <p className="lm-error">{error}</p>}
          {success && <p className="lm-success">{success}</p>}

          {/* Primary action button */}
          <button
            type="submit"
            className="lm-submit-btn"
            disabled={busy || !email}
          >
            {btnLabel()}
          </button>

        </form>

        {/* Switch mode */}
        <p className="lm-switch-text">
          {isSignIn ? (
            <>Don't have an account?{' '}
              <button type="button" className="lm-switch-link" onClick={() => switchMode('signup')}>Sign up</button>
            </>
          ) : (
            <>Already have an account?{' '}
              <button type="button" className="lm-switch-link" onClick={() => switchMode('signin')}>Sign in</button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
